import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Image from '../../components/images/Image';
import Loading from '../../components/common/Loading';
import { apiRequest, formatDate, shortDate } from '../../utils';
import { readCart, writeCart, cartCount, computeTotals } from './cart';

const money = (v, currency) => `${(Number(v) || 0).toFixed(2)} ${currency || ''}`.trim();

// Which sellable edition the buyer picked, kept across the shop → checkout hop.
export const EVENT_KEY = 'shopEventId';

const Shop = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [orders, setOrders] = useState(null);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null); // 'shopUnavailable' | 'noEvent' | generic

  // Which edition the catalog is for. The shop sells the session event AND the brand's
  // upcoming editions, because after an edition has run the session event is a PAST one
  // with nothing left on sale — that is what made this page render empty. The server
  // decides what is sellable; this is only which of those the buyer is looking at, kept
  // in sessionStorage so the choice survives the hop to checkout and back.
  const [eventId, setEventId] = useState(() => sessionStorage.getItem(EVENT_KEY) || null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await apiRequest('GET', `/shop${eventId ? `?event_id=${encodeURIComponent(eventId)}` : ''}`);
        if (cancelled) return;
        setData(res);
        setProblem(null);
        // The server may hand back a DIFFERENT event than asked for (the stored one is
        // sold out, or over) — follow it rather than keeping a stale id around.
        if (res.event_id) {
          setEventId(res.event_id);
          sessionStorage.setItem(EVENT_KEY, res.event_id);
        }
        // The basket is per edition: two editions of the same event sell different
        // rate ids, so one shared cart would silently mix them.
        setCart(readCart(res.event_id));
      } catch (err) {
        if (cancelled) return;
        // Non-contact persons get 403; send them home. Otherwise show a state.
        const msg = err.message || 'somethingWentWrong';
        if (msg === 'notAuthorized') {
          navigate('/home');
          return;
        }
        setProblem(['shopUnavailable', 'noEvent', 'noBillingAddress'].includes(msg) ? msg : 'generic');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, eventId]);

  // What the company already ordered for this event (read-only history).
  useEffect(() => {
    apiRequest('GET', '/shop/orders')
      .then((r) => setOrders(r.orders || []))
      .catch(() => setOrders([]));
  }, []);

  const setQty = (rateId, qty) => {
    const next = { ...cart };
    if (qty > 0) next[rateId] = qty;
    else delete next[rateId];
    setCart(next);
    writeCart(next, data?.event_id);
  };

  const products = data?.products || [];
  const currency = data?.currency;
  const { subtotal, vat, total } = computeTotals(products, cart, data?.with_vat);
  const count = cartCount(cart);

  return (
    <Base activeNav="shop">
      <Title>
        <Icon name="shopping-cart" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('shop')}
      </Title>

      {loading && <Loading size="lg" />}

      {!loading && problem && (
        <Card className="text-center">
          <Icon name="info" theme="primary" style={{ fontSize: '1.6rem' }} />
          <p className="mt-2 mb-0">
            {problem === 'shopUnavailable' && t('shopUnavailable')}
            {problem === 'noEvent' && t('shopUnavailable')}
            {problem === 'generic' && t('somethingWentWrong')}
          </p>
        </Card>
      )}

      {/* WHICH edition you are buying for. Always named — two editions of the same
          event share a name and differ only by date, so the date is the label that
          actually tells them apart. The picker only appears when there is a choice. */}
      {!loading && !problem && data?.event_name && (
        <Card>
          <div className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {t('orderingFor')}
          </div>
          <div style={{ fontWeight: 600 }}>
            {data.event_name}
            {data.event_date && (
              <span className="text-muted" style={{ fontWeight: 400 }}>
                {' · '}{shortDate(data.event_date, i18n.language)}
              </span>
            )}
          </div>
          {data.events?.length > 1 && (
            <div className="d-flex flex-wrap mt-2" style={{ gap: '0.4rem' }}>
              {data.events.map((ev) => (
                <button
                  key={ev.event_id}
                  type="button"
                  className={ev.selected ? 'btn-primary p-2' : 'btn-light p-2'}
                  style={{ borderRadius: '6px', fontSize: '0.85rem', border: 'none' }}
                  onClick={() => {
                    if (ev.selected) return;
                    sessionStorage.setItem(EVENT_KEY, ev.event_id);
                    setEventId(ev.event_id);
                  }}
                >
                  {shortDate(ev.date, i18n.language) || ev.name}
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {!loading && !problem && products.length === 0 && (
        <Card className="text-center">
          <Icon name="shopping-cart" theme="muted" style={{ fontSize: '1.6rem' }} />
          <p className="mt-2 mb-0">{t('noProductsAvailable')}</p>
        </Card>
      )}

      {!loading && !problem && products.map((p) => {
        const qty = Number(cart[p.rate_id]) || 0;
        // A discount that runs out on a date sells itself: print the deadline
        // under the price. Open-ended discounts (no ValidTo) print nothing.
        const until = p.discount_pct ? shortDate(p.discount_until, i18n.language) : null;
        return (
          <Card key={p.rate_id} className="d-flex align-items-center" style={{ gap: '0.75rem' }}>
            {p.image_link
              ? <Image url={p.image_link} size="4rem" contain className="flex-shrink-0" />
              : <Icon name="box" theme="muted" style={{ fontSize: '2rem' }} />}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{p.name}</div>
              <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                {p.discount_pct ? (
                  <>
                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                      {money(p.unit_price, currency)}
                    </span>{' '}
                    <span style={{ fontWeight: 600, color: 'var(--bs-danger, #d9534f)' }}>
                      {money(p.effective_price, currency)}
                    </span>{' '}
                    <span className="badge bg-danger-subtle text-danger-emphasis" style={{ fontSize: '0.72rem' }}>
                      −{(Number(p.discount_pct) || 0).toFixed(0)}%{p.discount_label ? ` · ${p.discount_label}` : ''}
                    </span>
                  </>
                ) : (
                  money(p.unit_price, currency)
                )}
                {data?.with_vat ? ` (+${(Number(p.vat_rate) || 0).toFixed(0)}% ${t('vat')})` : ''}
              </div>
              {until && (
                <div style={{ fontSize: '0.78rem', fontWeight: 500, color: '#b45309' }}>
                  {t('discountUntil', { date: until })}
                </div>
              )}
              {p.tickets?.length > 0 && (
                <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: '0.15rem' }}>
                  {p.tickets.map((tk, i) => (
                    <span key={i}>
                      {i > 0 ? ' · ' : ''}{tk.emoji ? `${tk.emoji} ` : ''}{tk.quantity}× {tk.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
              <Button theme="light" p="1" mb="0" silent styles={{ width: '2.2rem' }}
                      onClick={() => setQty(p.rate_id, Math.max(0, qty - 1))} disabled={qty <= 0}>
                <Icon name="minus-small" />
              </Button>
              <span style={{ minWidth: '1.5rem', textAlign: 'center' }}>{qty}</span>
              <Button theme="primary" p="1" mb="0" silent styles={{ width: '2.2rem' }}
                      onClick={() => setQty(p.rate_id, Math.min(999, qty + 1))}>
                <Icon name="plus-small" theme="light" />
              </Button>
            </div>
          </Card>
        );
      })}

      {!loading && !problem && products.length > 0 && (
        <Card className="shop-summary">
          <div className="d-flex justify-content-between">
            <span>{t('subtotal')}</span><strong>{money(subtotal, currency)}</strong>
          </div>
          {data?.with_vat && (
            <div className="d-flex justify-content-between text-muted">
              <span>{t('vat')}</span><span>{money(vat, currency)}</span>
            </div>
          )}
          <div className="d-flex justify-content-between mt-1" style={{ fontSize: '1.1rem' }}>
            <span style={{ textTransform: 'capitalize' }}>{t('total')}</span><strong>{money(total, currency)}</strong>
          </div>
          <Button className="mt-3" disabled={count === 0} onClick={() => navigate('/shop/checkout')}>
            {t('checkout')} {count > 0 ? `(${count})` : ''}
          </Button>
          {data?.terms_url && (
            <div className="text-center mt-2" style={{ fontSize: '0.85rem' }}>
              <a href={data.terms_url} target="_blank" rel="noopener noreferrer" className="text-muted">
                {t('termsAndConditions')}
              </a>
            </div>
          )}
        </Card>
      )}

      {/* Straight through to what these orders already handed out. */}
      {!loading && !problem && (
        <Card>
          <button type="button" className="d-flex justify-content-between align-items-center w-100"
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
                  onClick={() => navigate('/shop/tickets')}>
            <span>
              <Icon name="ticket" theme="primary" className="me-2" />
              {t('yourTickets')}
            </span>
            <Icon name="angle-right" theme="muted" />
          </button>
        </Card>
      )}

      {orders && orders.length > 0 && (
        <>
          <Title heading="5" my="3">{t('yourOrders')}</Title>
          {orders.map((o) => (
            <Card key={o.order_id}>
              <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                <span className="text-muted">
                  {o.order_date ? formatDate(o.order_date) : ''}
                  {/* The list spans every edition on sale, so each row names its own. */}
                  {o.event_name ? ` · ${o.event_name}` : ''}
                </span>
                {o.invoice_number && <span>{o.is_proposal ? t('proposalNumber') : t('invoiceNumber')}: {o.invoice_number}</span>}
              </div>
              {o.lines.map((l, i) => (
                <div key={i} className="text-muted" style={{ fontSize: '0.9rem' }}>
                  {l.description} <span>×{l.qty}</span>
                </div>
              ))}
              <div className="d-flex justify-content-between mt-1">
                <strong style={{ textTransform: 'capitalize' }}>{t('total')}</strong><strong>{money(o.total, currency)}</strong>
              </div>
              {o.invoice_id && (
                <a
                  href={`/api/shop/orders/${o.order_id}/invoice`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-light p-2 mt-2 text-center"
                  style={{ display: 'inline-block', borderRadius: '6px', fontSize: '0.85rem', textDecoration: 'none' }}
                >
                  <Icon name="file-download" className="me-1" /> {o.is_proposal ? t('downloadProposal') : t('downloadInvoice')}
                </a>
              )}
            </Card>
          ))}
        </>
      )}
    </Base>
  );
};

export default Shop;
